import { FormControl, FormGroup, Validators } from '@angular/forms';
import { combineLatest } from 'rxjs';
import { Country, DateOnly, maxDateValidator, minDateValidator, RoleTypeEnum, setRequired, USA_CODE, USState } from '../core';
import { SearchPoiResult } from './search-poi-result';


const SPECIAL_CHARS_PATTERN: RegExp = /^[^<>%#&?,]+$/;
const SPECIAL_CHARS_TEXT = '<>%#&?,';
const SPECIAL_CHARS_LITE_PATTERN: RegExp = /^[^<>%#?]+$/;
const SPECIAL_CHARS_LITE_TEXT = '<>%#?';
const ten_digits: RegExp = /^\d{10}$/;
const email_maxLenght = 128;
const name_maxLength = 50;
const longName_maxLength = 200;
const license_maxLength = 17;

export const confirmEmailMismatch = 'confirmEmailMismatch';
export const emailAlreadyExists = 'emailAlreadyExists';
type HospitalCompany = string | SearchPoiResult;

export class UserRequestControl implements IUserRequestControl {
	email = new FormControl<string | null>(null, [Validators.required, Validators.maxLength(email_maxLenght), Validators.pattern(SPECIAL_CHARS_PATTERN), Validators.email]);
	confirmEmail = new FormControl<string | null>(null, [Validators.required, Validators.maxLength(email_maxLenght), Validators.pattern(SPECIAL_CHARS_PATTERN), Validators.email]);
	firstName = new FormControl<string | null>(null, [Validators.required, Validators.maxLength(name_maxLength), Validators.pattern(SPECIAL_CHARS_PATTERN)]);
	lastName = new FormControl<string | null>(null, [Validators.required, Validators.maxLength(name_maxLength), Validators.pattern(SPECIAL_CHARS_PATTERN)]);
	dateOfBirth = new FormControl<DateOnly | null>(null, [
		Validators.required,
		maxDateValidator(new DateOnly(new DateOnly().setFullYear(new DateOnly().getFullYear() - 18) + 86400000)),
		minDateValidator(new DateOnly(Date.UTC(1899, 12, 1)))
	]);
	dateOfBirthFormat = new FormControl<string | null>(null);
	country = new FormControl<Country | null>(null, [Validators.required]);
	roleType = new FormControl<RoleTypeEnum | null>(null, [Validators.required]);
	otherRole = new FormControl<string | null>(null); // conditional required
	practiceType = new FormControl<string | null>(null, [Validators.required]); // conditional required 
	npi = new FormControl<string | null>(null, [Validators.pattern(ten_digits)]); // conditional required
	stateCode1 = new FormControl<string | null>(null); // conditional required
	licenseNumber1 = new FormControl<string | null>(null, [Validators.maxLength(license_maxLength), Validators.pattern(SPECIAL_CHARS_PATTERN)]); // conditional required
	stateCode2 = new FormControl<string | null>(null); // conditional required
	licenseNumber2 = new FormControl<string | null>(null, [Validators.maxLength(license_maxLength), Validators.pattern(SPECIAL_CHARS_PATTERN)]); // conditional required
	stateCode3 = new FormControl<string | null>(null); // conditional required
	licenseNumber3 = new FormControl<string | null>(null, [Validators.maxLength(license_maxLength), Validators.pattern(SPECIAL_CHARS_PATTERN)]); // conditional required
	stateCode4 = new FormControl<string | null>(null); // conditional required
	licenseNumber4 = new FormControl<string | null>(null, [Validators.maxLength(license_maxLength), Validators.pattern(SPECIAL_CHARS_PATTERN)]); // conditional required
	stateCode5 = new FormControl<string | null>(null); // conditional required
	licenseNumber5 = new FormControl<string | null>(null, [Validators.maxLength(license_maxLength), Validators.pattern(SPECIAL_CHARS_PATTERN)]); // conditional required
	distributor = new FormControl<string | null>(null, [Validators.maxLength(longName_maxLength), Validators.pattern(SPECIAL_CHARS_PATTERN)]); // conditional required
	hospital = new FormControl<HospitalCompany | null>(null, [Validators.maxLength(longName_maxLength), Validators.pattern(SPECIAL_CHARS_LITE_PATTERN)]); // conditional required
	company = new FormControl<HospitalCompany | null>(null, [Validators.maxLength(longName_maxLength), Validators.pattern(SPECIAL_CHARS_LITE_PATTERN)]); // conditional required
	address = new FormControl<string | null>(null, [Validators.required, Validators.maxLength(longName_maxLength), Validators.pattern(SPECIAL_CHARS_LITE_PATTERN)]);
	city = new FormControl<string | null>(null, [Validators.required, Validators.maxLength(name_maxLength), Validators.pattern(SPECIAL_CHARS_PATTERN)]);
	stateProvince = new FormControl<string | null>(null, [Validators.maxLength(longName_maxLength), Validators.pattern(SPECIAL_CHARS_PATTERN)]); // conditional required
	usStateProvince = new FormControl<USState | null>(null); // conditional required
	postalCode = new FormControl<string | null>(null, [Validators.required, Validators.maxLength(name_maxLength), Validators.pattern(SPECIAL_CHARS_PATTERN)]);
	officePhone = new FormControl<string | null>(null, [Validators.required, Validators.maxLength(name_maxLength), Validators.pattern(SPECIAL_CHARS_PATTERN)]);
	mobilePhone = new FormControl<string | null>(null, [Validators.maxLength(name_maxLength), Validators.pattern(SPECIAL_CHARS_PATTERN)]);
};

export function setConditionalValidators(form: FormGroup<UserRequestControl>): void {
	combineLatest([form.controls.roleType.valueChanges, form.controls.country.valueChanges]).subscribe(([roleType, country]) => {
		setRequired(form.controls.otherRole, roleType === RoleTypeEnum.OTHERS);
		setRequired(form.controls.hospital, roleType === RoleTypeEnum.HCP);
		setRequired(form.controls.company, roleType === RoleTypeEnum.OTHERS);
		setRequired(form.controls.distributor, roleType === RoleTypeEnum.HCP);
		setRequired(form.controls.stateProvince, country?.code !== USA_CODE);
		setRequired(form.controls.usStateProvince, country?.code === USA_CODE);
		setRequired(form.controls.practiceType, roleType === RoleTypeEnum.HCP && country?.code === USA_CODE);
		setRequired(form.controls.npi, roleType === RoleTypeEnum.HCP && country?.code === USA_CODE);
		setRequired(form.controls.stateCode1, roleType === RoleTypeEnum.HCP && country?.code === USA_CODE);
		setRequired(form.controls.licenseNumber1, roleType === RoleTypeEnum.HCP && country?.code === USA_CODE);
	});
	for (let index = 2; index <= 5; index++) {
		(form.controls as any)['stateCode' + index].valueChanges.subscribe((val: string | null) => setRequired((form.controls as any)['licenseNumber' + index], !!val));
		(form.controls as any)['licenseNumber' + index].valueChanges.subscribe((val: string | null) => setRequired((form.controls as any)['stateCode' + index], !!val));
	}
	combineLatest([form.controls.email.valueChanges, form.controls.confirmEmail.valueChanges]).subscribe(([email, confirmEmail]) => {
		if (confirmEmail && (confirmEmail.toLowerCase() !== email?.toLowerCase())) {
			form.controls.confirmEmail.setErrors({ [`${confirmEmailMismatch}`]: null }, { emitEvent: false });
		} else {
			form.controls.confirmEmail.setValue(form.controls.confirmEmail.value, { emitEvent: false });
		}
	});
}

export interface IUserRequestControl {
	email: FormControl<string | null>;
	confirmEmail: FormControl<string | null>;
	firstName: FormControl<string | null>;
	lastName: FormControl<string | null>;
	dateOfBirth: FormControl<DateOnly | null>;
	dateOfBirthFormat: FormControl<string | null>;
	country: FormControl<Country | null>;
	roleType: FormControl<RoleTypeEnum | null>;
	otherRole: FormControl<string | null>;
	practiceType: FormControl<string | null>;
	npi: FormControl<string | null>;
	stateCode1: FormControl<string | null>;
	licenseNumber1: FormControl<string | null>;
	stateCode2: FormControl<string | null>;
	licenseNumber2: FormControl<string | null>;
	stateCode3: FormControl<string | null>;
	licenseNumber3: FormControl<string | null>;
	stateCode4: FormControl<string | null>;
	licenseNumber4: FormControl<string | null>;
	stateCode5: FormControl<string | null>;
	licenseNumber5: FormControl<string | null>;
	distributor: FormControl<string | null>;
	hospital: FormControl<HospitalCompany | null>;
	company: FormControl<HospitalCompany | null>;
	address: FormControl<string | null>;
	city: FormControl<string | null>;
	stateProvince: FormControl<string | null>;
	usStateProvince: FormControl<USState | null>;
	postalCode: FormControl<string | null>;
	officePhone: FormControl<string | null>;
	mobilePhone: FormControl<string | null>;
};

export interface UserRequestForm {
	email: string;
	confirmEmail: string;
	firstName: string;
	lastName: string;
	dateOfBirth: DateOnly;
	dateOfBirthFormat: string;
	country: Country;
	roleType: RoleTypeEnum;
	otherRole: string;
	practiceType: string;
	npi: string;
	stateCode1: string;
	licenseNumber1: string;
	stateCode2: string;
	licenseNumber2: string;
	stateCode3: string;
	licenseNumber3: string;
	stateCode4: string;
	licenseNumber4: string;
	stateCode5: string;
	licenseNumber5: string;
	distributor: string;
	hospital: HospitalCompany;
	company: HospitalCompany;
	address: string;
	city: string;
	stateProvince: string;
	usStateProvince: USState;
	postalCode: string;
	officePhone: string;
	mobilePhone: string;
}

export const userReqErrorMsgs: { [key in keyof UserRequestForm]: { [y: string]: string[] } | null } = {
	email: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_EMAIL'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_EMAIL', email_maxLenght.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_EMAIL', SPECIAL_CHARS_TEXT],
		[`${Validators.email.name}`]: ['VALIDATION_MESSAGE_FORMAT_EMAIL'],
		[`${emailAlreadyExists}`]: ['VALIDATION_MESSAGE_ALREADY_EXIST_EMAIL'],

	},
	confirmEmail: {
		[`${Validators.required.name}`]: ['VALIDALIDATION_MESSAGE_REQUIRED_CONFIRM_EMAIL'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_CONFIRM_EMAIL', email_maxLenght.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_CONFIRM_EMAIL', SPECIAL_CHARS_TEXT],
		[`${Validators.email.name}`]: ['VALIDATION_MESSAGE_FORMAT_CONFIRM_EMAIL'],
		[`${confirmEmailMismatch}`]: ['VALIDATION_MESSAGE_COMPARE_CONFIRM_EMAIL'],
	},
	firstName: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_FIRST_NAME'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_FIRST_NAME', name_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_FIRST_NAME', SPECIAL_CHARS_TEXT]
	},
	lastName: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_LAST_NAME'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_LAST_NAME', name_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_LAST_NAME', SPECIAL_CHARS_TEXT]
	},
	dateOfBirth: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_DATE_OF_BIRTH'],
		'maxDate': ['VALIDATION_MESSAGE_MAXDATE_DATE_OF_BIRTH'],
		'minDate': ['VALIDATION_MESSAGE_INVALID_DATE_OF_BIRTH']
	},
	dateOfBirthFormat: null,
	country: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_COUNTRY']
	},
	roleType: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_ROLE_TYPE']
	},
	otherRole: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_ROLE']
	},
	practiceType: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_PRACTICE_TYPE']
	},
	npi: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_NPI'],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_DIGIT_NPI']
	},
	stateCode1: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_STATE']
	},
	licenseNumber1: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_LICENSE_NUMBER'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_LICENSE_NUMBER', license_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_LICENSE_NUMBER', SPECIAL_CHARS_TEXT]
	},
	stateCode2: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_STATE']
	},
	licenseNumber2: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_LICENSE_NUMBER'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_LICENSE_NUMBER', license_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_LICENSE_NUMBER', SPECIAL_CHARS_TEXT]
	},
	stateCode3: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_STATE']
	},
	licenseNumber3: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_LICENSE_NUMBER'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_LICENSE_NUMBER', license_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_LICENSE_NUMBER', SPECIAL_CHARS_TEXT]
	},
	stateCode4: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_STATE']
	},
	licenseNumber4: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_LICENSE_NUMBER'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_LICENSE_NUMBER', license_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_LICENSE_NUMBER', SPECIAL_CHARS_TEXT]
	},
	stateCode5: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_STATE']
	},
	licenseNumber5: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_LICENSE_NUMBER'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_LICENSE_NUMBER', license_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_LICENSE_NUMBER', SPECIAL_CHARS_TEXT]
	},
	distributor: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_DISTRIBUTOR'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_DISTRIBUTOR', longName_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_DISTRIBUTOR', SPECIAL_CHARS_TEXT]
	},
	hospital: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_HOSPITAL'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_HOSPITAL', longName_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_HOSPITAL', SPECIAL_CHARS_LITE_TEXT]
	},
	company: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_COMPANY'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_COMPANY', longName_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_COMPANY', SPECIAL_CHARS_LITE_TEXT]
	},
	address: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_ADDRESS'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_ADDRESS', longName_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_ADDRESS', SPECIAL_CHARS_LITE_TEXT]
	},
	city: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_CITY'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_CITY', name_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_CITY', SPECIAL_CHARS_TEXT]
	},
	stateProvince: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_PROVINCE'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_PROVINCE', longName_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_PROVINCE', SPECIAL_CHARS_TEXT]
	},
	usStateProvince: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_PROVINCE']
	},
	postalCode: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_POSTAL_CODE'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_POSTAL_CODE', name_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_POSTAL_CODE', SPECIAL_CHARS_TEXT]
	},
	officePhone: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_PHONE'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_PHONE', name_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_PHONE', SPECIAL_CHARS_TEXT]
	},
	mobilePhone: {
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_MOBILE', name_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_MOBILE', SPECIAL_CHARS_TEXT]
	}
}
